import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    CircularProgress,
    Alert,
    Box,
    Typography,
    Avatar,
    Chip
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';

const UserSwitchModal = ({ open, onClose }) => {
    const { getActiveUsers, switchToUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [error, setError] = useState('');

    // Load users when modal opens
    useEffect(() => {
        if (open) {
            loadUsers();
        }
    }, [open]);

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            setError('');
            const activeUsers = await getActiveUsers();
            setUsers(activeUsers);
        } catch (err) {
            setError('Failed to load user list');
            console.error('Error loading users:', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleSwitch = async () => {
        if (!selectedUser) return;

        try {
            setLoading(true);
            setError('');
            await switchToUser(selectedUser.id);
            onClose();
            setSelectedUser(null);
        } catch (err) {
            setError(err.message || 'Failed to switch to user');
            console.error('Error switching user:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
            setSelectedUser(null);
            setError('');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                    Switch to User
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Select a user to switch to their account
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ mb: 2 }}>
                    <Autocomplete
                        options={users}
                        getOptionLabel={(option) => `${option.name} (${option.email})`}
                        value={selectedUser}
                        onChange={(event, newValue) => setSelectedUser(newValue)}
                        loading={loadingUsers}
                        disabled={loading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select User"
                                placeholder="Start typing name or email"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            return (
                                <Box component="li" key={key} {...optionProps} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ width: 32, height: 32 }}>
                                        {option.name?.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {option.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.email}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={option.role}
                                        size="small"
                                        color={option.role === 'admin' ? 'error' : 'default'}
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                </Box>
                            );
                        }}
                        noOptionsText="No users found"
                    />
                </Box>

                {selectedUser && (
                    <Box
                        sx={{
                            p: 2,
                            backgroundColor: 'background.default',
                            borderRadius: '8px',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Selected user:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 40, height: 40 }}>
                                {selectedUser.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {selectedUser.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {selectedUser.email} • {selectedUser.role}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    color="inherit"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSwitch}
                    disabled={!selectedUser || loading}
                    variant="contained"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: '120px'
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        'Connect'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserSwitchModal;